/**
 * TypicalConversationCard
 *
 * Distills the selected filter scope into "what does one typical
 * conversation look like?". Median values (p50) are the headline because
 * they're robust to outliers — one $5 power-user conversation doesn't
 * skew the typical user's $0.04 bill.
 *
 * Renders alongside the Per-conversation averages card. Together they
 * answer: "what does ONE conversation cost and use in tokens?"
 *
 * Reads from the same /insights/cost/per-conversation-averages endpoint
 * the Cost Breakdown tab already fetches — no new backend call needed.
 */

import { Card, Col, Row, Tooltip, Typography, theme } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import type { PerConversationAveragesResponse } from '../services/insightsService';

const { Text } = Typography;

function fmtUsd(n: number): string {
  if (n == null || isNaN(n)) return '$0';
  if (n >= 1) return `$${n.toFixed(2)}`;
  if (n > 0) return `$${n.toFixed(6)}`;
  return '$0';
}

function fmtN(n: number): string {
  if (n == null || isNaN(n)) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return Math.round(n).toLocaleString('en-US');
}

export interface TypicalConversationCardProps {
  averages: PerConversationAveragesResponse;
}

export function TypicalConversationCard({ averages }: TypicalConversationCardProps) {
  const { t } = useLanguage();
  const { token } = theme.useToken();

  if (averages.conversation_count === 0) return null;

  // Estimate median input / output from p50 if available, otherwise fall
  // back to mean. The repository falls back to mean-as-p50 on backends
  // without percentile_cont (SQLite tests) — see llm_call_event_repository.py.
  const medianCost = averages.p50_cost_usd || averages.avg_total_cost_usd;
  const medianInput = averages.p50_input_tokens || averages.avg_input_tokens;
  const medianOutput = averages.p50_output_tokens || averages.avg_output_tokens;
  // Cached tokens don't have a p50 — proportional scaling from the mean
  // is the honest approximation. If a separate p50_cache_read becomes
  // available later, drop in here.
  const medianCacheRead = averages.avg_cache_read_tokens * (medianInput / Math.max(averages.avg_input_tokens, 1));
  const medianCacheWrite = averages.avg_cache_creation_tokens * (medianInput / Math.max(averages.avg_input_tokens, 1));
  const medianTotal = medianInput + medianOutput + medianCacheRead + medianCacheWrite;

  return (
    <Card
      title={
        <span>
          {t('insights.typicalConversation.title')}
          <Text type="secondary" style={{ fontSize: 12, marginInlineStart: 8, fontWeight: 'normal' }}>
            — {averages.conversation_count} conversations
          </Text>
        </span>
      }
      data-testid="typical-conversation-card"
    >
      <Row gutter={[16, 16]} align="middle">
        {/* Headline cost */}
        <Col xs={24} md={8}>
          <div style={{
            padding: '12px 16px',
            background: token.colorBgLayout,
            borderRadius: 6,
            borderInlineStart: `4px solid ${token.colorPrimary}`,
          }}>
            <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('insights.medianCost')}
            </Text>
            <div style={{
              fontSize: 32,
              fontWeight: 700,
              color: token.colorPrimary,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1.1,
              marginTop: 4,
            }}>
              {fmtUsd(medianCost)}
            </div>
            <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
              <Tooltip title="p90 — heavy-usage conversations cost up to this much. Read directly from llm_call_events percentile_cont(0.9).">
                <span>p90 heavy: {fmtUsd(averages.p90_cost_usd || averages.avg_total_cost_usd * 1.5)}</span>
              </Tooltip>
              {averages.avg_total_cost_usd > 0 && (
                <>
                  {' · '}
                  <Tooltip title={t('insights.meanTip')}>
                    <span>mean: {fmtUsd(averages.avg_total_cost_usd)}</span>
                  </Tooltip>
                </>
              )}
            </Text>
          </div>
        </Col>

        {/* Total tokens */}
        <Col xs={24} md={8}>
          <div style={{
            padding: '12px 16px',
            background: token.colorBgLayout,
            borderRadius: 6,
            borderInlineStart: `4px solid #10b981`,
          }}>
            <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('insights.medianTotalTokens')}
            </Text>
            <div style={{
              fontSize: 32,
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1.1,
              marginTop: 4,
            }}>
              {fmtN(medianTotal)}
            </div>
            <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
              input + cache reads + cache writes + output
            </Text>
          </div>
        </Col>

        {/* Cache effectiveness */}
        <Col xs={24} md={8}>
          <div style={{
            padding: '12px 16px',
            background: token.colorBgLayout,
            borderRadius: 6,
            borderInlineStart: `4px solid ${averages.cache_hit_ratio > 0.6 ? '#10b981' :
              averages.cache_hit_ratio > 0.2 ? '#f59e0b' : '#94a3b8'}`,
          }}>
            <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('insights.cacheEffectiveness')}
            </Text>
            <div style={{
              fontSize: 32,
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1.1,
              marginTop: 4,
              color: averages.cache_hit_ratio > 0.6 ? '#10b981' :
                averages.cache_hit_ratio > 0.2 ? '#f59e0b' : '#94a3b8',
            }}>
              {(averages.cache_hit_ratio * 100).toFixed(0)}%
            </div>
            <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
              of input served from cache (90% discount on this slice)
            </Text>
          </div>
        </Col>
      </Row>

      {/* Token breakdown row — what makes up the median conversation */}
      <Row gutter={[12, 8]} style={{ marginTop: 16 }}>
        <Col xs={12} sm={6}>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>{t('insights.costTimeline.inputUncached')}</Text>
            <div style={{ fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {fmtN(medianInput)}
            </div>
          </div>
        </Col>
        <Col xs={12} sm={6}>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>{t('insights.costTimeline.cacheReads')}</Text>
            <div style={{
              fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
              color: medianCacheRead > 0 ? '#10b981' : undefined,
            }}>
              {fmtN(medianCacheRead)}
            </div>
          </div>
        </Col>
        <Col xs={12} sm={6}>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>{t('insights.costTimeline.cacheWrites')}</Text>
            <div style={{
              fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
              color: medianCacheWrite > 0 ? '#f59e0b' : undefined,
            }}>
              {fmtN(medianCacheWrite)}
            </div>
          </div>
        </Col>
        <Col xs={12} sm={6}>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>{t('insights.costTimeline.output')}</Text>
            <div style={{ fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {fmtN(medianOutput)}
            </div>
            <Text type="secondary" style={{ fontSize: 10 }}>
              what the model writes back
            </Text>
          </div>
        </Col>
      </Row>
    </Card>
  );
}
